import { FileApi, UserApi } from '@/apis';
import { getStorage, removeStorage, saveLocalStorage } from '@/utils/browsers';
import { FileNameUploadSplit } from '@/types/files';
import { keyStorage } from '@/configs/constants';

declare global {
    interface Window {
        google: any
    }
}

class GoogleAPI {
    private static instance: GoogleAPI;
    private userApi: UserApi;
    private fileApi: FileApi;
    private accessToken: string | null = null;
    private state: string;
    private token: string;

    private constructor() {
        this.userApi = new UserApi();
        this.fileApi = new FileApi();
        this.accessToken = getStorage(keyStorage.ACCESS_TOKEN_GOOGLE);
        this.state = getStorage(keyStorage.STATE);
        this.token = getStorage(keyStorage.TOKEN);
    }

    public static getInstance(): GoogleAPI {
        if (!GoogleAPI.instance) {
            GoogleAPI.instance = new GoogleAPI();
        }
        return GoogleAPI.instance;
    }

    public async setAccessToken(accessToken: string, state: string | null, token: string | null = null) {
        if (token) {
            saveLocalStorage(keyStorage.TOKEN, token);
            this.token = token;
        }
        if (state) {
            saveLocalStorage(keyStorage.STATE, state);
            this.state = state;
        }
        this.accessToken = accessToken;
        saveLocalStorage(keyStorage.ACCESS_TOKEN_GOOGLE, accessToken);
        return true;
    }

    public async getToken(): Promise<string | undefined | null> {
        const tokenGG = getStorage(keyStorage.ACCESS_TOKEN_GOOGLE);
        const state = getStorage(keyStorage.STATE);
        const token = getStorage(keyStorage.TOKEN);
        if (!tokenGG) {
            return undefined;
        }
        const check = await this.validateToken(tokenGG);
        if (check) {
            this.accessToken = tokenGG;
            return tokenGG;
        }

        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/rest/api/v1/system/refresh-token`, {
                method: 'POST',
                headers: { 'Authorization': `${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    state: state
                })

            });
            if (res.status === 200) {
                const data = await res.json();
                const { access_token } = data;
                this.accessToken = access_token;
                saveLocalStorage(keyStorage.ACCESS_TOKEN_GOOGLE, access_token);
                return access_token;
            }
        } catch (error) {
            return undefined;
        }
        return undefined;
    }

    private async validateToken(token: string): Promise<boolean> {
        try {
            const response = await fetch(`https://www.googleapis.com/oauth2/v3/tokeninfo?access_token=${token}`);
            if (response.status !== 200) {
                return false;
            }
            return true;
        } catch (error) {
            return false;
        }
    }

    public async signOutGG(): Promise<void> {
        try {
            if (this.accessToken) {
                await fetch(`https://oauth2.googleapis.com/revoke?token=${this.accessToken}`, {
                    method: 'POST',
                    headers: { 'Content-type': 'application/x-www-form-urlencoded' }
                });
            }
            removeStorage([keyStorage.ACCESS_TOKEN_GOOGLE, keyStorage.STATE]);
            this.state = '';
            this.accessToken = null;
        } catch (error) {
            console.error('Error revoking token:', error);
        }
    }

    public async checkPermissionUploadById(folderId: string | undefined, email: string | undefined): Promise<boolean> {
        const accessToken = await this.getToken();
        if (!folderId || !email || !accessToken) {
            console.error('Không thể kiểm tra quyền: Thiếu folderId, email hoặc access token.');
            return false;
        }

        try {
            const metadataResponse = await fetch(`https://www.googleapis.com/drive/v3/files/${folderId}?fields=trashed`, {
                headers: { 'Authorization': `Bearer ${accessToken}` }
            });
            const metadata = await metadataResponse.json();
            if (metadata.trashed) return false;

            const permissionsResponse = await fetch(
                `https://www.googleapis.com/drive/v3/files/${folderId}/permissions?fields=permissions(id,emailAddress,role,type)`,
                {
                    headers: { 'Authorization': `Bearer ${accessToken}` }
                }
            );
            const data = await permissionsResponse.json();
            const permissions = data.permissions;
            if (!permissions) return false;

            const rolesEdit = ['owner', 'writer'];
            const user = permissions.find((e: any) => e.emailAddress === email);
            return !!user && rolesEdit.includes(user.role);
        } catch (error) {
            console.error('Lỗi khi lấy danh sách quyền:', error);
            return false;
        }
    }

    private async getFolderId(cusId: string): Promise<string | undefined> {
        const res: any = await this.fileApi.getFolderIDCustomer(cusId);
        return res?.data?.data?.folder_url_id ? String(res.data.data.folder_url_id) : undefined;
    }

    public async deleteFolder(cusId: string, id: number): Promise<boolean> {
        const accessToken = await this.getToken();
        if (!cusId || !id || !accessToken) return false;

        try {
            const folder_url_id = await this.getFolderId(cusId);
            const resDel: any = await this.userApi.deleteUser({ id });
            if (resDel?.data?.ok) {
                if (folder_url_id) {
                    try {
                        const response = await fetch(`https://www.googleapis.com/drive/v3/files/${folder_url_id}`, {
                            method: 'DELETE',
                            headers: { 'Authorization': `Bearer ${accessToken}` }
                        });
                        if (!response.ok) {
                            console.error('Lỗi khi xoá folder trên Drive:', await response.text());
                        }
                    } catch (error) {
                        console.error('Error deleting folder on Drive:', error);
                    }
                }
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error deleting user or folder:', error);
            return false;
        }
    }

    public async deleteFileById(fileId: string): Promise<boolean> {
        const accessToken = await this.getToken();
        if (!fileId || !accessToken) return false;
        try {
            const response = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${accessToken}` }
            });
            return response.ok;
        } catch (error) {
            console.error('Error deleting file:', error);
            return false;
        }
    }

    public async deleteFolderByFolderId(folderId: string): Promise<boolean> {
        const accessToken = await this.getToken();
        if (!folderId || !accessToken) return false;
        try {
            const response = await fetch(`https://www.googleapis.com/drive/v3/files/${folderId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${accessToken}` }
            });
            return response.ok;
        } catch (error) {
            console.error('Error deleting folder by ID:', error);
            return false;
        }
    }

    public async checkFolderExist(folderId: string | undefined, parentFolderId: string): Promise<boolean> {
        const accessToken = await this.getToken();
        if (!folderId || !accessToken) return false;

        try {
            const query = `'${parentFolderId}' in parents and mimeType = 'application/vnd.google-apps.folder' and trashed = false`;
            const response = await fetch(
                `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&fields=files(id)`,
                {
                    headers: { 'Authorization': `Bearer ${accessToken}` }
                }
            );
            const data = await response.json();
            const folders = data.files;
            if (!folders) return false;
            return folders.some((folder: any) => folder.id === folderId);
        } catch (error) {
            console.error('Error checking folder existence:', error);
            return false;
        }
    }

    public async checkFileExist(nameFile: string, parentId: string): Promise<string | undefined> {
        const accessToken = await this.getToken();
        if (!accessToken) return undefined;

        try {
            const query = `'${parentId}' in parents and name = '${nameFile}' and trashed = false`;
            const response = await fetch(
                `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&fields=files(id,name)`,
                {
                    headers: { 'Authorization': `Bearer ${accessToken}` }
                }
            );
            const data = await response.json();
            const files = data.files;
            if (!files || files.length === 0) return undefined;
            return files[0].id;
        } catch (error) {
            console.error('Error checking file existence:', error);
            return undefined;
        }
    }

    public async createFolderInParent(folderName: string, parentId: string): Promise<string | undefined> {
        const accessToken = await this.getToken();
        if (!accessToken) return undefined;

        try {
            const response = await fetch('https://www.googleapis.com/drive/v3/files', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    name: folderName,
                    mimeType: 'application/vnd.google-apps.folder',
                    parents: [parentId]
                })
            });
            const data = await response.json();
            return data.id;
        } catch (error) {
            console.error('Error creating folder:', error);
            return undefined;
        }
    }

    public async createFolderRoot(): Promise<string | undefined> {
        const accessToken = await this.getToken();
        if (!accessToken) return undefined;

        try {
            const createResponse = await fetch('https://www.googleapis.com/drive/v3/files', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    name: 'Onlinepost',
                    mimeType: 'application/vnd.google-apps.folder'
                })
            });
            const createData = await createResponse.json();
            const folderId = createData.id;

            if (folderId) {
                await fetch(`https://www.googleapis.com/drive/v3/files/${folderId}/permissions`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        role: 'reader',
                        type: 'anyone',
                        allowFileDiscovery: false
                    })
                });
                return folderId;
            }
            return undefined;
        } catch (error) {
            console.error('Error creating folder or setting permission:', error);
            return undefined;
        }
    }

    public async findFolderByNameAndParentId(parentId: string, name: string): Promise<string | null> {
        const accessToken = await this.getToken();
        if (!accessToken) return null;

        try {
            const query = `mimeType='application/vnd.google-apps.folder' and name='${name}' and '${parentId}' in parents and trashed=false`;
            const response = await fetch(
                `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&fields=files(id,name)`,
                {
                    headers: { 'Authorization': `Bearer ${accessToken}` }
                }
            );
            const data = await response.json();
            const folder = data.files ? data.files.find((file: any) => file.name === name) : undefined;

            if (folder) {
                return folder.id;
            } else {
                return await this.createFolderInParent(name, parentId) ?? null;
            }
        } catch (error) {
            console.error('Error fetching folder ID:', error);
            return null;
        }
    }

    public async checkFolderAccess(folderId: string | undefined): Promise<boolean> {
        const accessToken = await this.getToken();
        if (!folderId || !accessToken) return false;

        try {
            const response = await fetch(`https://www.googleapis.com/drive/v3/files/${folderId}?fields=name`, {
                headers: { 'Authorization': `Bearer ${accessToken}` }
            });
            return response.ok;
        } catch (error) {
            console.error('Error fetching folder name:', error);
            return false;
        }
    }

    public async checkFileNameInput(name: string): Promise<boolean> {
        if (!name || name.length > 255) return false;
        const dataSplit: FileNameUploadSplit | undefined = await this.splitNameFile(name);
        if (!dataSplit) return false;
        const regex = /^\d{4}(0[1-9]|1[0-2])$/;
        return regex.test(dataSplit.monthFolder);
    }

    public async splitNameFile(name: string): Promise<FileNameUploadSplit | undefined> {
        const regex = /^[^_]+_\d{6}_.*\.pdf$/;
        if (!name || !regex.test(name)) return undefined;
        const indexComma = name.lastIndexOf('.');
        const baseName = name.slice(0, indexComma);
        const extension = name.slice(indexComma);
        const subParts = baseName.split('_');
        if (subParts.length >= 3) {
            const firstPart = subParts[0];
            const secondPart = subParts[1];
            const thirdPart = subParts.slice(2).join('_') + (extension ?? '.pdf');
            const data: FileNameUploadSplit = {
                userFolder: firstPart,
                monthFolder: secondPart,
                fileName: thirdPart
            };
            return data;
        }
        return undefined;
    }

    public async findExistingFile(name: string, parentFolderId: string): Promise<string | undefined> {
        const accessToken = await this.getToken();
        if (!accessToken) return undefined;

        try {
            const query = `'${parentFolderId}' in parents and name = '${name}' and trashed = false`;
            const response = await fetch(
                `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&fields=files(id,name)`,
                {
                    headers: { 'Authorization': `Bearer ${accessToken}` }
                }
            );
            const data = await response.json();
            const files = data.files;
            if (!files || files.length === 0) return undefined;
            return files[0].id;
        } catch (error) {
            console.error('Error finding existing file:', error);
            return undefined;
        }
    }
}

export default GoogleAPI;
